/**
 * The curative pipeline's view of a lead (contract v2): which branch it is on and why, who decides, the
 * family the obituary names, how the score was built, and whether a phone has been found.
 *
 * Read-only. Everything here is `leads.extra.curative`, written by `make import-curative`; nothing in
 * the app derives from it. Outreach on these leads is call-only, so the only links are lookups a person
 * opens by hand (TruePeopleSearch, Zillow) — never anything that sends.
 */

import { Badge, Card } from "@/components/ui";

type Part = { points?: number; max?: number; reason?: string };
type Person = { name?: string; relation?: string; city?: string | null; state?: string | null; status?: string };
type Lookup = { name?: string; relation?: string; tps_url?: string };

export type Curative = {
  branch?: string;
  branch_label?: string;
  decision_maker?: { name?: string | null; role?: string; relation?: string | null };
  evidence?: {
    quote?: string;
    source?: { paper?: string; url?: string | null; issue_date?: string | null };
    obituary?: { site?: string | null; url?: string | null } | null;
  };
  people?: Person[];
  score?: {
    motivation?: number;
    multiplier?: number;
    multiplier_reason?: string;
    workability?: Part;
    total?: number;
    parts?: Record<string, Part>;
  };
  phone_status?: string;
  phone_found?: { phone_e164?: string; source?: string } | null;
  lookup_links?: Lookup[];
  zillow_url?: string | null;
  tier_now?: string;
};

const EXT = { target: "_blank", rel: "noopener noreferrer" } as const;
const label = (k: string) => k.replace(/_/g, " ");
const where = (p: Person) => [p.city, p.state].filter(Boolean).join(", ");

type ParcelLine = { situs_display?: string | null; situs_raw?: string | null; property_type?: string };

export function CurativeCard({ curative: c, parcel }: { curative: Curative; parcel?: ParcelLine }) {
  const dm = c.decision_maker;
  const ev = c.evidence ?? {};
  const s = c.score ?? {};
  const people = c.people ?? [];
  const sourceUrl = ev.source?.url ?? ev.obituary?.url ?? null;
  return (
    <Card title="Curative" data-testid="curative-card" className="mb-3">
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={c.branch === "A" ? "ok" : "warn"} data-testid="curative-branch">
            Branch {c.branch ?? "?"}
          </Badge>
          <span className="text-fg-muted">{c.branch_label}</span>
          {c.tier_now ? <Badge tone="muted">curative tier now {c.tier_now}</Badge> : null}
        </div>

        {parcel ? (
          <div data-testid="curative-property">
            <span className="font-semibold">Property: </span>
            {parcel.situs_display ?? parcel.situs_raw ?? "—"}
            {parcel.property_type ? <span className="text-fg-muted"> · {label(parcel.property_type)}</span> : null}
          </div>
        ) : null}

        <div>
          <span className="font-semibold">Decision-maker: </span>
          {dm?.name ? (
            <>
              {dm.name} <span className="text-fg-muted">— {dm.role}</span>
            </>
          ) : (
            <span className="text-fg-muted">{dm?.role ?? "unknown — research"}</span>
          )}
        </div>

        {ev.quote || sourceUrl ? (
          <blockquote className="border-l-2 border-border pl-3 text-fg-muted">
            {ev.quote ? <>“{ev.quote}” </> : null}
            {sourceUrl ? (
              <a href={sourceUrl} {...EXT} className="text-brand underline">
                {ev.quote ? `${ev.source?.paper ?? "source"} ${ev.source?.issue_date ?? ""}` : (ev.obituary?.site ?? "obituary")}
              </a>
            ) : null}
          </blockquote>
        ) : null}

        {people.length ? (
          <table className="w-full text-left" data-testid="curative-people">
            <thead className="text-fg-muted">
              <tr>
                <th className="font-normal">Person</th>
                <th className="font-normal">Relation</th>
                <th className="font-normal">Where</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p, i) => (
                <tr key={`${p.name}-${i}`} className={p.status === "preceded" ? "text-fg-muted" : ""}>
                  <td>{p.name}</td>
                  <td>
                    {p.relation}
                    {p.status === "preceded" ? " (preceded in death)" : ""}
                  </td>
                  <td>{where(p)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        <table className="w-full text-left" data-testid="curative-score">
          <thead className="text-fg-muted">
            <tr>
              <th className="font-normal">Component</th>
              <th className="font-normal">Points</th>
              <th className="font-normal">Why</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(s.parts ?? {}).map(([k, p]) => (
              <tr key={k}>
                <td>{label(k)}</td>
                <td>
                  {p.points}/{p.max}
                </td>
                <td className="text-fg-muted">{p.reason}</td>
              </tr>
            ))}
            <tr>
              <td>multiplier</td>
              <td>×{s.multiplier?.toFixed(2)}</td>
              <td className="text-fg-muted">{s.multiplier_reason}</td>
            </tr>
            <tr>
              <td>workability</td>
              <td>
                {s.workability?.points}/{s.workability?.max}
              </td>
              <td className="text-fg-muted">{s.workability?.reason}</td>
            </tr>
            <tr className="font-semibold">
              <td>total</td>
              <td>{s.total}/100</td>
              <td className="font-normal text-fg-muted">motivation {s.motivation} + workability</td>
            </tr>
          </tbody>
        </table>

        <div data-testid="curative-phone">
          <span className="font-semibold">Phone: </span>
          {c.phone_found?.phone_e164 ? (
            <>
              {c.phone_found.phone_e164} <span className="text-fg-muted">· {c.phone_found.source} · not DNC-checked</span>
            </>
          ) : (
            <Badge tone="danger">HUMAN SEARCH NEEDED</Badge>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          {(c.lookup_links ?? []).map((l, i) =>
            l.tps_url ? (
              <a key={`${l.name}-${i}`} href={l.tps_url} {...EXT} className="text-brand underline">
                TPS: {l.name} ({l.relation})
              </a>
            ) : null,
          )}
          {c.zillow_url ? (
            <a href={c.zillow_url} {...EXT} className="text-brand underline">
              Zillow (Zestimate, as-is condition unverified)
            </a>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
